import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Card, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import Page from '../../../components/Page';
import GlobalTable from '../../../components/CustomTableWithPagination/GlobalTable';
import { IconBadge, SoftChip, StatTile } from '../components/ui';
import { HERO_GRADIENT, RT } from '../theme/retentionTheme';

// Shared shell for the four Retention sidebar lists (§2.4): header, KPI tiles, pill filter, GlobalTable.
// The row's Open action goes to openPath(row) when given, else the student's workspace Home (/student-journey/:id).
// Returns `reload` through onReady so a page can refetch after its own row actions (e.g. Remind parent).
export default function RetentionListPage({
  title,
  icon,
  subtitle,
  headers,
  fetchThunk,
  resource,
  kpis = [],
  filters = [],
  filterParam = 'filter',
  side,
  endpoint,
  openPath,
  onReady,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [filter, setFilter] = useState(filters[0]?.value ?? '');
  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 50,
    search: '',
    sort: { field: '', direction: '' },
    frozenColumns: [],
    hiddenColumns: [],
  });

  const params = useMemo(
    () => ({
      page: tableState.page,
      limit: tableState.pageSize,
      search: tableState.search,
      [filterParam]: filter,
    }),
    [tableState.page, tableState.pageSize, tableState.search, filter, filterParam],
  );

  const reload = useCallback(() => dispatch(fetchThunk(params)), [dispatch, fetchThunk, params]);
  useEffect(() => {
    reload();
    onReady?.(reload);
  }, [reload, onReady]);

  const handleAction = useCallback(
    (actionKey, row) => {
      if (actionKey === 'open' && row?.course_enrollment_id) navigate(openPath ? openPath(row) : `/student-journey/${row.course_enrollment_id}`);
    },
    [navigate, openPath],
  );

  const rows = Array.isArray(resource?.data) ? resource.data : [];
  // the endpoint chip only explains a failed load (e.g. schema missing on this database)
  const notBuilt = !resource?.loading && Boolean(resource?.error) && endpoint;

  return (
    <Page title={title}>
      <Stack gap={2.5}>
        {/* header */}
        <Card sx={{ overflow: 'hidden' }}>
          <Stack direction="row" alignItems="center" gap={2} sx={{ p: 2.5, background: HERO_GRADIENT, color: '#fff' }}>
            <IconBadge icon={icon} color="#fff" size={48} sx={{ bgcolor: 'rgba(255,255,255,.18)', color: '#fff' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="overline" sx={{ opacity: 0.8 }}>
                Retention
              </Typography>
              <Typography variant="h4" sx={{ color: '#fff' }}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
            {notBuilt && (
              <SoftChip
                label={endpoint}
                icon="solar:code-square-bold"
                color="#fff"
                sx={{ bgcolor: 'rgba(255,255,255,.16)', fontFamily: 'monospace', display: { xs: 'none', md: 'inline-flex' } }}
              />
            )}
          </Stack>
        </Card>

        {kpis.length > 0 && (
          <Stack direction="row" gap={2} flexWrap="wrap">
            {kpis.map((k) => (
              <StatTile key={k.label} icon={k.icon} color={k.color} label={k.label} value={resource?.meta?.summary?.[k.key]} hint={k.hint} />
            ))}
          </Stack>
        )}

        <Stack direction={{ xs: 'column', lg: 'row' }} gap={2.5} alignItems="flex-start">
          <Card sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            {filters.length > 0 && (
              <Stack direction="row" alignItems="center" gap={1.5} sx={{ p: 2, pb: 0 }} flexWrap="wrap">
                <ToggleButtonGroup size="small" exclusive value={filter} onChange={(e, v) => v !== null && setFilter(v)}>
                  {filters.map((f) => (
                    <ToggleButton key={f.label} value={f.value}>
                      {f.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            )}
            <Box sx={{ p: 1 }}>
              <GlobalTable
                rowUniqueId="row_id"
                headers={headers}
                rows={rows}
                meta={resource?.meta}
                loading={resource?.loading}
                tableState={tableState}
                setTableState={setTableState}
                onButtonActionClick={handleAction}
              />
            </Box>
          </Card>
          {side ? <Stack sx={{ width: { xs: '100%', lg: 340 }, flexShrink: 0 }} gap={2.5}>{side}</Stack> : null}
        </Stack>
      </Stack>
    </Page>
  );
}

export const LIST_COLORS = { blue: '#2563EB', violet: '#7C3AED', green: RT.success, amber: RT.warning, red: RT.error, teal: '#0E7C86', indigo: RT.primary };
